import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { listTirages, createTirage, updateTirage, deleteTirage } from '../../api/tirages';
import './TiragesPage.css';

const emptyForm = { name: '', start_time: '', end_time: '', is_active: true };

// Libre-service (demande explicite 2026-09-22, etendue le meme jour) : chaque
// company peut agir sur TOUS les tirages qu'elle voit dans sa liste, y compris
// les GLOBAUX (ex. "NY MIDI") — jamais en les modifiant vraiment (le global
// reste inchange pour les autres companies), mais en creant/modifiant SA
// PROPRE version (meme code) qui prend le dessus UNIQUEMENT pour elle (cf.
// Tirage.effective_for cote backend — meme principe que les numeros gagnants).
// `is_override` (renvoye par l'API, calcule cote backend) distingue 3 cas :
//  - is_global=true                    -> pas encore personnalise, "Modifier"
//                                          crée une COPIE privee (POST, meme code).
//  - is_global=false, is_override=true -> deja personnalise, "Modifier" edite
//                                          cette copie (PATCH), "Supprimer" =
//                                          revenir au global (le supprime, le
//                                          global reapparait tout seul au reload).
//  - is_global=false, is_override=false-> tirage 100% prive (aucun fallback
//                                          global), "Modifier"/"Supprimer" normaux.
const TiragesPage = () => {
  const [tirages, setTirages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [editId, setEditId] = useState(null);
  const [editIsNew, setEditIsNew] = useState(false); // true = on personnalise un global (POST), false = on edite le sien (PATCH)
  const [editForm, setEditForm] = useState(emptyForm);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    listTirages()
      .then(setTirages)
      .catch(() => setError('Impossible de charger les tirages.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Code automatique (demande explicite 2026-09-22) : continue sur le plus
  // grand code VISIBLE (global + les tiens) + 1 — plus besoin de le taper à
  // la main. Ex. si le plus grand code vu est 13, le prochain tirage ajouté
  // sera 14. Non modifiable côté client — le champ code n'existe même plus
  // dans le formulaire d'ajout.
  const nextCode = useMemo(
    () => (tirages.length ? Math.max(...tirages.map((t) => t.code)) + 1 : 1),
    [tirages],
  );

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateEditField = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    try {
      await createTirage({
        code: nextCode,
        name: form.name,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        is_active: form.is_active,
      });
      setForm(emptyForm);
      load();
    } catch (err) {
      setError('Creation impossible.');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditIsNew(t.is_global);
    setEditForm({
      code: String(t.code),
      name: t.name,
      start_time: t.start_time || '',
      end_time: t.end_time || '',
      is_active: t.is_active,
    });
    setError('');
  };

  const saveEdit = async () => {
    setBusyId(editId);
    setError('');
    try {
      const payload = {
        code: Number(editForm.code),
        name: editForm.name,
        start_time: editForm.start_time || null,
        end_time: editForm.end_time || null,
        is_active: editForm.is_active,
      };
      if (editIsNew) {
        await createTirage(payload);
      } else {
        await updateTirage(editId, payload);
      }
      setEditId(null);
      load();
    } catch (err) {
      setError(editIsNew ? 'Personnalisation impossible.' : 'Modification impossible.');
    } finally {
      setBusyId(null);
    }
  };

  const removeTirage = async (t) => {
    const msg = t.is_override
      ? `Revenir au tirage global pour "${t.code}-${t.name}" ? Ta version personnalisée sera supprimée (les fiches déjà vendues sont conservées et rattachées au tirage global).`
      : `Supprimer le tirage "${t.code}-${t.name}" ?`;
    if (!window.confirm(msg)) return;
    setBusyId(t.id);
    setError('');
    try {
      await deleteTirage(t.id);
      load();
    } catch (err) {
      // Message du serveur (ex. tirage qui a deja des fiches -> refuse).
      let detail = '';
      try { detail = JSON.parse(err.message)?.detail || ''; } catch { /* message brut */ }
      setError(detail || 'Suppression impossible.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <p className="tp-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/parametres" className="bc-link">Parametres</Link>
        {' >> '}
        <span className="bc-current">Mes Tirages</span>
      </p>

      <p className="tp-note">
        Tu peux modifier <strong>n'importe quel tirage de cette liste</strong>, y compris
        les tirages <strong>Global</strong> (gérés par le superadmin, ex. NY MIDI) : ça crée
        ta propre version (mêmes règles, heures modifiables) — les autres companies ne voient
        aucun changement. Ajoute aussi des tirages 100% à toi via le formulaire en bas.
      </p>

      {error && <p className="tp-error">{error}</p>}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <table className="tp-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Heure début</th>
              <th>Heure fin</th>
              <th>Actif</th>
              <th>Portée</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tirages.map((t) => (
              editId === t.id ? (
                <tr key={t.id}>
                  <td>
                    <input
                      className="tp-input tp-input-inline tp-input-code"
                      type="number"
                      value={editForm.code}
                      onChange={(e) => updateEditField('code', e.target.value)}
                      disabled={editIsNew}
                      title={editIsNew ? 'Le code reste celui du tirage global personnalisé' : ''}
                    />
                  </td>
                  <td>
                    <input
                      className="tp-input tp-input-inline"
                      value={editForm.name}
                      onChange={(e) => updateEditField('name', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="tp-input tp-input-inline"
                      type="time"
                      value={editForm.start_time}
                      onChange={(e) => updateEditField('start_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="tp-input tp-input-inline"
                      type="time"
                      value={editForm.end_time}
                      onChange={(e) => updateEditField('end_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => updateEditField('is_active', e.target.checked)}
                    />
                  </td>
                  <td>{editIsNew ? 'Sera privé' : 'Privé'}</td>
                  <td className="tp-actions">
                    <button type="button" onClick={saveEdit} disabled={busyId === editId}>
                      Enregistrer
                    </button>
                    <button type="button" onClick={() => setEditId(null)}>Annuler</button>
                  </td>
                </tr>
              ) : (
                <tr key={t.id}>
                  <td>{t.code}</td>
                  <td>{t.name}</td>
                  <td>{t.start_time || '—'}</td>
                  <td>{t.end_time || '—'}</td>
                  <td>{t.is_active ? 'Oui' : 'Non'}</td>
                  <td>
                    <span className={t.is_global ? 'tp-badge tp-badge-global' : 'tp-badge tp-badge-prive'}>
                      {t.is_global ? 'Global' : (t.is_override ? 'Personnalisé' : 'Privé')}
                    </span>
                  </td>
                  <td className="tp-actions">
                    <button type="button" onClick={() => startEdit(t)}>
                      {t.is_global ? 'Personnaliser' : 'Modifier'}
                    </button>
                    {!t.is_global && (
                      <button
                        type="button"
                        className="tp-del"
                        onClick={() => removeTirage(t)}
                        disabled={busyId === t.id}
                      >
                        {t.is_override ? 'Revenir au global' : 'Supprimer'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            ))}
            {tirages.length === 0 && (
              <tr><td colSpan={7} className="tp-empty">Aucun tirage.</td></tr>
            )}
          </tbody>
        </table>
      )}

      <form className="tp-form" onSubmit={handleCreate}>
        <div className="tp-form-title">Ajouter un tirage privé</div>
        <div className="tp-row">
          <label className="tp-label">Code</label>
          <input className="tp-input tp-input-code" type="number" value={nextCode} disabled />
          <span className="tp-hint">automatique</span>
        </div>
        <div className="tp-row">
          <label className="tp-label">Nom</label>
          <input
            className="tp-input"
            type="text"
            placeholder="ex: MON TIRAGE"
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
          />
        </div>
        <div className="tp-row">
          <label className="tp-label">Heure début</label>
          <input
            className="tp-input"
            type="time"
            value={form.start_time}
            onChange={(e) => updateField('start_time', e.target.value)}
          />
        </div>
        <div className="tp-row">
          <label className="tp-label">Heure fin</label>
          <input
            className="tp-input"
            type="time"
            value={form.end_time}
            onChange={(e) => updateField('end_time', e.target.value)}
          />
        </div>
        <label className="tp-checkbox-label">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => updateField('is_active', e.target.checked)}
          />
          Actif
        </label>
        <button type="submit" className="tp-add-btn" disabled={creating}>
          {creating ? 'Création...' : '+ Ajouter un tirage'}
        </button>
      </form>
    </Layout>
  );
};

export default TiragesPage;
